import { OrdersHeader } from "../components/OrdersHeader/OrdersHeader";
import { OrdersFilters } from "../components/OrdersFilters/OrdersFilters";
import { OrdersTable } from "../components/OrdersTable/OrdersTable";
import { OrdersBulkActionBar } from "../components/OrdersBulkActionBar/OrdersBulkActionBar";
import { ChangeDateModal } from "../components/ChangeDateModal/ChangeDateModal";
import { CreateOrderModal } from "../components/CreateOrderModal/CreateOrderModal";
import { useOrderSelection } from "../hooks/useOrderSelection";
import { useEffect, useState } from "react";
import { getOrders } from "../api/orders";
import type { Order } from "../types/order.types";
import type { PaginatedResponse } from "@/shared/types/paginatedResponse";
import {
  getAirports,
  getFilters,
  type Airport,
  type SystemDictionary,
} from "@/shared/api/filters";
import { toast } from "sonner";

export interface OrdersPageFiltersState {
  airline: string;
  status: string;
  page: number;
  paymentStatus: string;
  pageSize: number;
  dateFrom: string;
  dateTo: string;
  fromIata: string;
  toIata: string;
}

export function OrdersPage() {
  const [ordersPage, setOrdersPage] = useState<PaginatedResponse<Order> | null>(
    null
  );
  const [dictionary, setDictionary] = useState<SystemDictionary | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [airports, setAirports] = useState<Airport[]>([]);
  const [isChangeDateModalOpen, setIsChangeDateModalOpen] = useState(false);
  const [isCreateOrderModalOpen, setIsCreateOrderModalOpen] = useState(false);
  const [filters, setFilters] = useState<OrdersPageFiltersState>({
    airline: "",
    status: "",
    page: 1,
    paymentStatus: "",
    pageSize: 20,
    dateFrom: "",
    dateTo: "",
    toIata: "",
    fromIata: "",
  });

  const { selectedOrderIds, toggleOrder, toggleAll, clearSelection } =
    useOrderSelection();

  const fetchOrders = async () => {
    clearSelection();
    try {
      setIsLoading(true);
      setError(null);

      const res = await getOrders(filters);
      const dictionary = await getFilters();
      const airports = await getAirports();
      setAirports(airports);
      setDictionary(dictionary);
      setOrdersPage(res);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Ошибка";
      toast.error(message);
      console.error(e);
      setError("Не удалось загрузить заказы");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [filters]);

  const orders = ordersPage?.data ?? [];
  const pageOrderIds = orders.map((o) => String(o.id));
  const isAllSelected =
    pageOrderIds.length > 0 && pageOrderIds.every((id) => selectedOrderIds.has(id));
  const isSomeSelected =
    pageOrderIds.some((id) => selectedOrderIds.has(id)) && !isAllSelected;

  const handleChangeDateSuccess = (
    updatedIds: string[],
    departureAt: string,
    arrivalAt: string
  ) => {
    setOrdersPage((prev) =>
      prev
        ? {
            ...prev,
            data: prev.data.map((order) =>
              updatedIds.includes(String(order.id))
                ? { ...order, departureDate: departureAt, returnDate: arrivalAt }
                : order
            ),
          }
        : null
    );
    clearSelection();
    setIsChangeDateModalOpen(false);
  };

  return (
    <div className="max-w-[1400px] mx-auto">
      <OrdersHeader onCreateOrder={() => setIsCreateOrderModalOpen(true)} />
      <OrdersFilters
        totalCount={ordersPage?.total ?? 1}
        filteredCount={ordersPage?.data.length ?? 1}
        dictionary={dictionary}
        setFilters={setFilters}
        filters={filters}
        airports={airports}
      />
      <OrdersBulkActionBar
        selectedCount={selectedOrderIds.size}
        onDeselectAll={clearSelection}
        onChangeDates={() => setIsChangeDateModalOpen(true)}
      />
      <OrdersTable
        orders={orders}
        totalPages={ordersPage?.totalPages ?? 1}
        currentPage={ordersPage?.page ?? 1}
        page={filters.page}
        pageSize={filters.pageSize}
        onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
        selectedOrderIds={selectedOrderIds}
        onToggleOrder={toggleOrder}
        onToggleAll={() => toggleAll(pageOrderIds)}
        isAllSelected={isAllSelected}
        isSomeSelected={isSomeSelected}
      />
      <ChangeDateModal
        isOpen={isChangeDateModalOpen}
        onClose={() => setIsChangeDateModalOpen(false)}
        selectedOrderIds={selectedOrderIds}
        onSuccess={handleChangeDateSuccess}
      />
      <CreateOrderModal
        key={String(isCreateOrderModalOpen)}
        open={isCreateOrderModalOpen}
        onClose={() => setIsCreateOrderModalOpen(false)}
        onSuccess={() => {
          setIsCreateOrderModalOpen(false);
          fetchOrders();
        }}
      />
    </div>
  );
}
